// Basic
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
// Store and actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsPlans from '@/app/modules/plans/api/actionsPlans';
import useSessionStore from '@/app/store/session';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import DetailsPanel from '@/app/modules/plans/components/panels/DetailsPanel';
import ModalAddAllowedClients from '@/app/modules/plans/components/modals/addAllowedClients/ModalAddAllowedClients';
import ModalUpdatePlan from '@/app/modules/plans/components/modals/updatePlan/ModalUpdatePlan';
import AllowedClientsPanel from '@/app/modules/plans/components/panels/AllowedClientsPanel';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/common-utils/components/ui/accordion';
// Common
import RouteUrls from '../../enums/RouteUrls';
import Permissions from '@/app/permissions/Permissions';
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
// Lang
import { nsPlan } from '@/app/i18n/namespaces';

/**
 * @returns {JSX} PlanDetails
 */
function PlanDetails() {

    // Constants
    const initialQuery = getInitialQuery();

    // State
    const [showModalAddAllowedClients, setShowModalAddAllowedClients] = useState(false);
    const [showModalUpdatePlan, setShowModalUpdatePlan] = useState(false);
    const [launchRefetchDetails, setLaunchRefetchDetails] = useState(true);
    const [launchRefetchClients, setLaunchRefetchClients] = useState(true);
    const [openPlanDetails, setOpenPlanDetails] = useState(true);
    const [openPlanClients, setOpenPlanClients] = useState(false);

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const { permissions: userPermissions } = useSessionStore(s => s.session) ?? {};


    // URL Params
    const { planId } = useParams();

    // API Calls
    const { data: plan, isLoading, isError, refetch: refetchPlan } = actionsPlans.getPlan({ organizationId: rootOrganization.uuid, planId }, true);
    const {
        data: allowedClientsData,
        isLoading: isLoadingAllowedClients,
        isFetching: isFetchingAllowedClients,
        refetch: refetchAllowedClients
    } = actionsPlans.getAllowedClients({ query: initialQuery, organizationId: rootOrganization.uuid, planId: planId }, false);

    // Functions
    const hasAllowedClientsListPermission = hasRequiredPermissions({
        permissions: userPermissions,
        requiredPermissions: [Permissions.SUBSCRIPTION_PLAN_LIST_ALLOWED_CLIENTS_FOR_CLIENT_PLAN.name]
    });

    // Hooks
    useEffect(() => {
        refetchPlan(true, rootOrganization.uuid, planId);
        setLaunchRefetchDetails(false);
    }, [launchRefetchDetails]);

    useEffect(() => {
        refetchAllowedClients(false, { query: initialQuery, organizationId: rootOrganization.uuid, planId: planId });
        setLaunchRefetchClients(false);
    }, [launchRefetchClients]);

    if (isError) {
        return <Navigate to={RouteUrls.PLANS.url} />;
    }

    return (
        <Page>
            <Page.Header
                route={RouteUrls.PLAN_DETAILS}
                routeBase={RouteUrls.PLANS}
            />
            {
                isLoading ?
                    <Spinner className={'full-screen-loading'} />
                    :
                    <Page.Body dataCy='page-plan-details'>
                        <Accordion type='multiple' defaultValue={['item-1']}>
                            <AccordionItem className='rounded-md' value='item-1' >
                                <AccordionTrigger
                                    className='opacity-[.8]'
                                    open={openPlanDetails}
                                    onClick={() => setOpenPlanDetails(!openPlanDetails)}
                                >
                                    <div className='flex items-center gap-2'>
                                        <Icon icon={ICONS.PLAN} />
                                        <Entity entity={'page.plans.accordions.details'} />
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <>
                                        <PageActions
                                            actions={[{
                                                icon: ICONS.EDIT,
                                                entity: 'actions.page.update_offer',
                                                namespace: nsPlan,
                                                onClick: () => setShowModalUpdatePlan(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: userPermissions,
                                                    requiredPermissions: [Permissions.SUBSCRIPTION_PLAN_UPDATE.name]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            }]}
                                        />
                                        <DetailsPanel plan={plan} />
                                    </>
                                </AccordionContent>
                            </AccordionItem>
                            {hasAllowedClientsListPermission && (
                                <AccordionItem className='rounded-md' value='item-2' >
                                    <AccordionTrigger
                                        className='opacity-[.8]'
                                        open={openPlanClients}
                                        onClick={() => setOpenPlanClients(!openPlanClients)}
                                    >
                                        <div className='flex items-center gap-2'>
                                            <Icon icon={ICONS.USER} />
                                            <Entity entity={'page.plans.accordions.allowed_clients'} />
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <>
                                            <PageActions
                                                actions={[{
                                                    icon: ICONS.ADD,
                                                    entity: 'actions.page.add_clients',
                                                    namespace: nsPlan,
                                                    onClick: () => setShowModalAddAllowedClients(true),
                                                    visible: true,
                                                    tooltipForbidden: 'title.permissions.access_denied',
                                                    disabled: !hasRequiredPermissions({
                                                        permissions: userPermissions,
                                                        requiredPermissions: [Permissions.SUBSCRIPTION_PLAN_ADD_CLIENT.name]
                                                    })
                                                }]}
                                            />
                                            <AllowedClientsPanel
                                                data={allowedClientsData}
                                                isLoading={isLoadingAllowedClients}
                                                isFetching={isFetchingAllowedClients}
                                                setLaunchRefetch={setLaunchRefetchClients}
                                            />
                                        </>
                                    </AccordionContent>
                                </AccordionItem>
                            )}
                        </Accordion>
                    </Page.Body>
            }
            {
                showModalAddAllowedClients &&
                <ModalAddAllowedClients
                    isOpen={showModalAddAllowedClients}
                    setIsOpen={setShowModalAddAllowedClients}
                    setLaunchRefetch={setLaunchRefetchClients}
                />
            }
            {
                showModalUpdatePlan &&
                <ModalUpdatePlan
                    isOpen={showModalUpdatePlan}
                    setIsOpen={setShowModalUpdatePlan}
                    plan={plan}
                    setLaunchRefetch={setLaunchRefetchDetails}
                />
            }
        </Page>
    );
}

export default PlanDetails;